<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignBatchDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignBatch/CampaignBatchDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const showCreateDialog = ref(false);
const deletingBatchId = ref(null);
const confirmDeleteRef = ref(null);

const uiFlags = useMapGetter('campaignBatches/getUIFlags');
const batches = useMapGetter('campaignBatches/getBatches');

const isFetching = computed(() => uiFlags.value.isFetching);
const hasNoBatches = computed(
  () => batches.value?.length === 0 && !isFetching.value
);

onMounted(() => {
  store.dispatch('campaignBatches/get');
});

const statusConfig = {
  draft: { class: 'text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-slate-700/40', icon: 'i-lucide-file-edit', label: 'Rascunho' },
  ready: { class: 'text-blue-700 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/30', icon: 'i-lucide-check-circle', label: 'Pronto' },
  sending: { class: 'text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/30', icon: 'i-lucide-loader animate-spin', label: 'Enviando' },
  completed: { class: 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-900/30', icon: 'i-lucide-check-check', label: 'Concluído' },
  failed: { class: 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30', icon: 'i-lucide-alert-circle', label: 'Falhou' },
};

const getStatusConfig = status => statusConfig[status] || statusConfig.draft;

const statusLabel = status => {
  const cfg = getStatusConfig(status);
  return cfg.label || status;
};

const sourceLabel = sourceType => {
  const map = {
    crm_deals: 'CRM',
    csv_import: 'CSV',
    manual: 'Manual',
  };
  return map[sourceType] || sourceType;
};

const sourceIcon = sourceType => {
  const map = {
    crm_deals: 'i-lucide-git-branch',
    csv_import: 'i-lucide-file-spreadsheet',
    manual: 'i-lucide-hand',
  };
  return map[sourceType] || 'i-lucide-layers';
};

const navigateToBatch = batch => {
  router.push({
    name: 'campaigns_batch_detail',
    params: { batchId: batch.id },
  });
};

const handleCreateSuccess = () => {
  showCreateDialog.value = false;
  store.dispatch('campaignBatches/get');
};

const handleDelete = batch => {
  deletingBatchId.value = batch.id;
  confirmDeleteRef.value?.open();
};

const confirmDelete = async () => {
  try {
    await store.dispatch('campaignBatches/delete', deletingBatchId.value);
    useAlert(t('CAMPAIGN.BATCH.DELETE.SUCCESS'));
  } catch (e) {
    useAlert(t('CAMPAIGN.BATCH.DELETE.ERROR'));
  }
  deletingBatchId.value = null;
  confirmDeleteRef.value?.close();
};

const formatDate = dateStr => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const relativeTime = dateStr => {
  if (!dateStr) return '';
  const now = Date.now();
  const d = new Date(dateStr).getTime();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min atrás`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h atrás`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}d atrás`;
};

// Stats
const totalContacts = computed(() =>
  (batches.value || []).reduce((sum, b) => sum + (b.contacts_count || 0), 0)
);
const completedCount = computed(() =>
  (batches.value || []).filter(b => b.status === 'completed').length
);
const sendingCount = computed(() =>
  (batches.value || []).filter(b => b.status === 'sending').length
);
</script>

<template>
  <CampaignLayout :header-title="t('CAMPAIGN.BATCH.HEADER_TITLE')" :button-label="t('CAMPAIGN.BATCH.NEW_BATCH')"
    @click="showCreateDialog = true" @close="showCreateDialog = false">
    <template #action>
      <CampaignBatchDialog v-if="showCreateDialog" @close="showCreateDialog = false" @success="handleCreateSuccess" />
    </template>

    <div v-if="isFetching" class="flex items-center justify-center py-20">
      <Spinner />
    </div>

    <div v-else-if="hasNoBatches" class="flex flex-col items-center pt-20 gap-5">
      <div class="size-20 rounded-2xl bg-gradient-to-br from-violet-100 to-indigo-100 dark:from-violet-900/30 dark:to-indigo-900/30 flex items-center justify-center">
        <span class="i-lucide-package size-9 text-violet-500" />
      </div>
      <div class="text-center">
        <p class="text-lg font-semibold text-n-slate-12 mb-1">
          {{ t('CAMPAIGN.BATCH.EMPTY_STATE.TITLE') }}
        </p>
        <p class="text-sm text-n-slate-10 max-w-sm">
          {{ t('CAMPAIGN.BATCH.EMPTY_STATE.SUBTITLE') }}
        </p>
      </div>
      <Button label="Criar Primeiro Lote" icon="i-lucide-plus" @click="showCreateDialog = true" />
    </div>

    <div v-else class="flex flex-col gap-6">
      <div class="grid grid-cols-3 gap-3">
        <div class="rounded-xl border border-n-weak bg-white dark:bg-n-solid-2 px-4 py-3">
          <div class="flex items-center gap-2 mb-1">
            <span class="i-lucide-package size-4 text-violet-500" />
            <span class="text-xs text-n-slate-9 font-medium">Total de Lotes</span>
          </div>
          <span class="text-2xl font-bold text-n-slate-12">{{ batches.length }}</span>
        </div>
        <div class="rounded-xl border border-n-weak bg-white dark:bg-n-solid-2 px-4 py-3">
          <div class="flex items-center gap-2 mb-1">
            <span class="i-lucide-users size-4 text-blue-500" />
            <span class="text-xs text-n-slate-9 font-medium">Contatos</span>
          </div>
          <span class="text-2xl font-bold text-n-slate-12">{{ totalContacts }}</span>
        </div>
        <div class="rounded-xl border border-n-weak bg-white dark:bg-n-solid-2 px-4 py-3">
          <div class="flex items-center gap-2 mb-1">
            <span class="i-lucide-check-check size-4 text-emerald-500" />
            <span class="text-xs text-n-slate-9 font-medium">Concluídos</span>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="text-2xl font-bold text-n-slate-12">{{ completedCount }}</span>
            <span v-if="sendingCount > 0" class="text-xs text-amber-600 font-medium">
              {{ sendingCount }} enviando
            </span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <div
          v-for="batch in batches"
          :key="batch.id"
          class="group rounded-xl border border-n-weak bg-white dark:bg-n-solid-2 hover:border-n-slate-6 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
          @click="navigateToBatch(batch)"
        >
          <div class="px-4 py-3.5">
            <div class="flex items-start justify-between gap-3">
              <div class="flex items-start gap-3 min-w-0 flex-1">
                <div class="size-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                  :class="getStatusConfig(batch.status).class">
                  <span :class="getStatusConfig(batch.status).icon" class="size-5" />
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2 mb-1">
                    <h3 class="text-sm font-semibold text-n-slate-12 truncate">
                      {{ batch.name }}
                    </h3>
                    <span class="text-[11px] font-medium px-2 py-0.5 rounded-full inline-flex items-center gap-1 flex-shrink-0"
                      :class="getStatusConfig(batch.status).class">
                      <span :class="getStatusConfig(batch.status).icon" class="size-3" />
                      {{ statusLabel(batch.status) }}
                    </span>
                  </div>
                  <div class="flex items-center flex-wrap gap-x-3 gap-y-1">
                    <span class="inline-flex items-center gap-1 text-xs text-n-slate-10">
                      <span class="i-lucide-users size-3" />
                      <span class="font-semibold text-n-slate-12">{{ batch.contacts_count }}</span>
                      contatos
                    </span>
                    <span class="inline-flex items-center gap-1 text-xs text-n-slate-10">
                      <span :class="sourceIcon(batch.source_type)" class="size-3" />
                      {{ sourceLabel(batch.source_type) }}
                    </span>
                    <span v-if="batch.category" class="inline-flex items-center gap-1 text-xs text-n-slate-10">
                      <span class="i-lucide-tag size-3" />
                      {{ batch.category }}
                    </span>
                    <span class="inline-flex items-center gap-1 text-xs text-n-slate-9">
                      <span class="i-lucide-calendar size-3" />
                      {{ formatDate(batch.created_at) }}
                    </span>
                  </div>
                </div>
              </div>

              <div v-if="batch.status === 'completed' || batch.status === 'sending'" class="flex items-center gap-1.5 flex-shrink-0 mt-1">
                <span v-if="batch.sent_count" class="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400">
                  <span class="i-lucide-check size-3" />
                  {{ batch.sent_count }}
                </span>
                <span v-if="batch.failed_count" class="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                  <span class="i-lucide-x size-3" />
                  {{ batch.failed_count }}
                </span>
                <span v-if="batch.skipped_count" class="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                  <span class="i-lucide-skip-forward size-3" />
                  {{ batch.skipped_count }}
                </span>
              </div>
            </div>
          </div>

          <div
            v-if="batch.status === 'sending' && batch.contacts_count > 0"
            class="h-1 bg-n-alpha-1"
          >
            <div
              class="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-500"
              :style="{ width: Math.min(((batch.sent_count || 0) / batch.contacts_count) * 100, 100) + '%' }"
            />
          </div>

          <div
            class="flex items-center justify-between px-4 py-2 border-t border-n-weak/50 bg-n-alpha-1/30"
            @click.stop
          >
            <span class="text-[11px] text-n-slate-9">
              {{ relativeTime(batch.created_at) }}
            </span>
            <div class="flex items-center gap-1.5">
              <button
                class="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                @click="navigateToBatch(batch)"
              >
                <span class="i-lucide-external-link size-3" />
                Detalhes
              </button>
              <button
                v-if="batch.status === 'draft' || batch.status === 'ready'"
                class="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                @click="handleDelete(batch)"
              >
                <span class="i-lucide-trash-2 size-3" />
                Excluir
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Dialog ref="confirmDeleteRef" :title="t('CAMPAIGN.BATCH.DELETE.TITLE')"
      :description="t('CAMPAIGN.BATCH.DELETE.DESCRIPTION')" :confirm-button-label="t('CAMPAIGN.BATCH.DELETE.CONFIRM')"
      type="alert" @confirm="confirmDelete" />
  </CampaignLayout>
</template>
